
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useLanguage } from '@/hooks/useLanguage';

interface InstallationSectionProps {
  form: any;
  isLoading: boolean;
  allowedStatuses: string[];
}

const InstallationSection: React.FC<InstallationSectionProps> = ({
  form,
  isLoading,
  allowedStatuses
}) => {
  const { translate } = useLanguage();

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle>{translate('subscriptions.installationDetails')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium">{translate('subscriptions.installationAddress')}</h3>
            <FormField
              control={form.control}
              name="installation_street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate('subscriptions.street')}</FormLabel>
                  <FormControl>
                    <Input placeholder={translate('subscriptions.street')} disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="installation_zip_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate('subscriptions.zipCode')}</FormLabel>
                    <FormControl>
                      <Input placeholder={translate('subscriptions.zipCode')} disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="installation_city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate('subscriptions.city')}</FormLabel>
                    <FormControl>
                      <Input placeholder={translate('subscriptions.city')} disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="housing_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate('subscriptions.housingType')}</FormLabel>
                  <Select 
                    disabled={isLoading}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={translate('common.select')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="house">{translate('subscriptions.house')}</SelectItem>
                      <SelectItem value="apartment">{translate('subscriptions.apartment')}</SelectItem>
                      <SelectItem value="office">{translate('subscriptions.office')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">{translate('subscriptions.subscriptionStatus')}</h3>
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate('common.status')}</FormLabel>
                  <Select 
                    disabled={isLoading}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={translate('common.select')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {allowedStatuses.map(status => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="activation_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{translate('subscriptions.activationDate')}</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                          type="button"
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>{translate('subscriptions.pickDate')}</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-0" 
                      align="start" 
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={isLoading}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    {translate('subscriptions.activationDate')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstallationSection;
